import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Calendar, DollarSign, User, FileText, Package, CreditCard} from 'react-feather';
import dayjs from 'dayjs';
import { purchaseReturnActions } from '../../action/purchaseReturnActions';

const ViewPurchaseReturn = ({ returnData, onClose }) => {
    const [returnDetails, setReturnDetails] = useState(returnData);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('details');

    // Fetch full details if needed
    useEffect(() => {
        if (returnData?.id && !returnData.items) {
            fetchFullDetails();
        }
    }, [returnData?.id]);

    const fetchFullDetails = async () => {
        try {
            setLoading(true);
            const result = await purchaseReturnActions.getReturn(returnData.id);
            if (result.success) {
                setReturnDetails(result.data);
            }
        } catch (error) {
            console.error('Error fetching return details:', error);
        } finally {
            setLoading(false);
        }
    };

    // Format date
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD MMM YYYY');
    };

    // Format date with time
    const formatDateTime = (dateString) => {
        if (!dateString) return 'N/A';
        return dayjs(dateString).format('DD MMM YYYY hh:mm A');
    };

    // Format currency
    const formatCurrency = (amount) => {
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            minimumFractionDigits: 2
        }).format(amount || 0);
    };

    // Get status badge
    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase() || '';
        switch (statusLower) {
            case 'pending':
                return <span className="badge bg-warning">Pending</span>;
            case 'approved':
                return <span className="badge bg-info">Approved</span>;
            case 'received':
                return <span className="badge bg-success">Received</span>;
            case 'rejected':
                return <span className="badge bg-danger">Rejected</span>;
            case 'completed':
                return <span className="badge bg-success">Completed</span>;
            case 'cancelled':
                return <span className="badge bg-secondary">Cancelled</span>;
            default:
                return <span className="badge bg-secondary">Unknown</span>;
        }
    };

    // Get payment status badge
    const getPaymentStatusBadge = (paymentStatus) => {
        switch (paymentStatus?.toLowerCase()) {
            case 'paid':
                return <span className="badge bg-success">Paid</span>;
            case 'partial':
                return <span className="badge bg-warning">Partial</span>;
            case 'unpaid':
                return <span className="badge bg-danger">Unpaid</span>;
            default:
                return <span className="badge bg-secondary">Unknown</span>;
        }
    };

    if (loading) {
        return (
            <div className="modal fade show" style={{ display: 'block' }} id="view-purchase-return">
                <div className="modal-dialog modal-dialog-centered modal-xl">
                    <div className="modal-content">
                        <div className="modal-header">
                            <h5 className="modal-title">View Purchase Return</h5>
                            <button type="button" className="btn-close" onClick={onClose}></button>
                        </div>
                        <div className="modal-body text-center py-5">
                            <div className="spinner-border text-primary" role="status">
                                <span className="visually-hidden">Loading...</span>
                            </div>
                            <p className="mt-3">Loading return details...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (!returnDetails) return null;

    return (
        <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }} id="view-purchase-return">
            <div className="modal-dialog modal-dialog-centered modal-xl">
                <div className="modal-content">
                    <div className="modal-header">
                        <h5 className="modal-title">
                            Purchase Return Details - {returnDetails.reference_no}
                        </h5>
                        <button 
                            type="button" 
                            className="btn-close" 
                            onClick={onClose}
                            aria-label="Close"
                        ></button>
                    </div>
                    
                    <div className="modal-body">
                        {/* Tabs Navigation */}
                        <ul className="nav nav-tabs mb-4">
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'details' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('details')}
                                >
                                    <FileText size={16} className="me-2" />
                                    Return Details
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'items' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('items')}
                                >
                                    <Package size={16} className="me-2" />
                                    Return Items ({returnDetails.items?.length || 0})
                                </button>
                            </li>
                            <li className="nav-item">
                                <button
                                    className={`nav-link ${activeTab === 'payments' ? 'active' : ''}`}
                                    onClick={() => setActiveTab('payments')}
                                >
                                    <CreditCard size={16} className="me-2" />
                                    Payment Summary
                                </button>
                            </li>
                        </ul>

                        {/* Tab Content */}
                        {activeTab === 'details' && (
                            <div className="tab-pane active">
                                {/* Status Cards */}
                                <div className="row mb-4">
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body py-3">
                                                <small className="text-muted d-block">Return Status</small>
                                                <h5 className="mb-0 mt-1">{getStatusBadge(returnDetails.status)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body py-3">
                                                <small className="text-muted d-block">Payment Status</small>
                                                <h5 className="mb-0 mt-1">{getPaymentStatusBadge(returnDetails.payment_status)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body py-3">
                                                <small className="text-muted d-block">Return Date</small>
                                                <h5 className="mb-0 mt-1">{formatDate(returnDetails.return_date)}</h5>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-3">
                                        <div className="card bg-light">
                                            <div className="card-body py-3">
                                                <small className="text-muted d-block">Created By</small>
                                                <h5 className="mb-0 mt-1">{returnDetails.created_by || 'Admin'}</h5>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Basic Information */}
                                <div className="row">
                                    <div className="col-md-6">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">
                                                    <Package size={16} className="me-2" />
                                                    Return Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <table className="table table-sm table-borderless">
                                                    <tbody>
                                                        <tr>
                                                            <td width="40%"><strong>Return Reference:</strong></td>
                                                            <td>{returnDetails.reference_no}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Original Purchase:</strong></td>
                                                            <td>{returnDetails.original_reference_no || returnDetails.purchase_reference || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Purchase Date:</strong></td>
                                                            <td>{formatDate(returnDetails.original_purchase_date)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Return Date:</strong></td>
                                                            <td>{formatDate(returnDetails.return_date)}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Main Reason:</strong></td>
                                                            <td>{returnDetails.reason || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Status:</strong></td>
                                                            <td>{getStatusBadge(returnDetails.status)}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                    
                                    <div className="col-md-6">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">
                                                    <User size={16} className="me-2" />
                                                    Supplier Information
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <table className="table table-sm table-borderless">
                                                    <tbody>
                                                        <tr>
                                                            <td width="40%"><strong>Supplier Name:</strong></td>
                                                            <td>{returnDetails.supplier_name || returnDetails.supplier || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Supplier Phone:</strong></td>
                                                            <td>{returnDetails.supplier_phone || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Supplier Email:</strong></td>
                                                            <td>{returnDetails.supplier_email || 'N/A'}</td>
                                                        </tr>
                                                        <tr>
                                                            <td><strong>Supplier Address:</strong></td>
                                                            <td>{returnDetails.supplier_address || 'N/A'}</td>
                                                        </tr>
                                                    </tbody>
                                                </table>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Financial Summary */}
                                <div className="row">
                                    <div className="col-12">
                                        <div className="card mb-3">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">
                                                    <DollarSign size={16} className="me-2" />
                                                    Financial Summary
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-2">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Subtotal</small>
                                                            <h6 className="mb-0 text-primary">{formatCurrency(returnDetails.subtotal)}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Tax</small>
                                                            <h6 className="mb-0">{formatCurrency(returnDetails.tax)}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Discount</small>
                                                            <h6 className="mb-0">{formatCurrency(returnDetails.discount)}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-2">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Shipping</small>
                                                            <h6 className="mb-0">{formatCurrency(returnDetails.shipping || 0)}</h6>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="text-center p-2 border rounded bg-light">
                                                            <small className="text-muted d-block">Grand Total</small>
                                                            <h5 className="mb-0 text-primary fw-bold">{formatCurrency(returnDetails.grand_total)}</h5>
                                                        </div>
                                                    </div>
                                                </div>
                                                
                                                <div className="row mt-3">
                                                    <div className="col-md-4">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Paid Amount</small>
                                                            <h5 className="mb-0 text-success">{formatCurrency(returnDetails.paid_amount)}</h5>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Due Amount</small>
                                                            <h5 className={`mb-0 ${returnDetails.due_amount > 0 ? 'text-danger' : 'text-success'}`}>
                                                                {formatCurrency(returnDetails.due_amount)}
                                                            </h5>
                                                        </div>
                                                    </div>
                                                    <div className="col-md-4">
                                                        <div className="text-center p-2 border rounded">
                                                            <small className="text-muted d-block">Payment Status</small>
                                                            <h5 className="mb-0 mt-1">{getPaymentStatusBadge(returnDetails.payment_status)}</h5>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                {/* Notes */}
                                {returnDetails.notes && (
                                    <div className="row">
                                        <div className="col-12">
                                            <div className="card mb-3">
                                                <div className="card-header bg-light">
                                                    <h6 className="mb-0">
                                                        <FileText size={16} className="me-2" />
                                                        Notes
                                                    </h6>
                                                </div>
                                                <div className="card-body">
                                                    <p className="mb-0">{returnDetails.notes}</p>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                )}

                                {/* Timestamps */}
                                <div className="row">
                                    <div className="col-12">
                                        <div className="card">
                                            <div className="card-header bg-light">
                                                <h6 className="mb-0">
                                                    <Calendar size={16} className="me-2" />
                                                    Timestamps
                                                </h6>
                                            </div>
                                            <div className="card-body">
                                                <div className="row">
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Created:</small>
                                                        <strong>{formatDateTime(returnDetails.created_at)}</strong>
                                                    </div>
                                                    <div className="col-md-6">
                                                        <small className="text-muted d-block">Last Updated:</small>
                                                        <strong>{formatDateTime(returnDetails.updated_at)}</strong>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'items' && (
                            <div className="tab-pane active">
                                <div className="card">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">
                                            <Package size={16} className="me-2" />
                                            Return Items ({returnDetails.items?.length || 0})
                                        </h6>
                                    </div>
                                    <div className="card-body p-0">
                                        {returnDetails.items && returnDetails.items.length > 0 ? (
                                            <div className="table-responsive">
                                                <table className="table table-hover table-sm mb-0">
                                                    <thead className="bg-light">
                                                        <tr>
                                                            <th>#</th>
                                                            <th>Product</th>
                                                            <th>SKU</th>
                                                            <th className="text-end">Original Qty</th>
                                                            <th className="text-end">Return Qty</th>
                                                            <th className="text-end">Unit Price</th>
                                                            <th className="text-end">Discount</th>
                                                            <th className="text-end">Tax</th>
                                                            <th className="text-end">Total</th>
                                                            <th>Reason</th>
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        {returnDetails.items.map((item, index) => (
                                                            <tr key={item.id || index}>
                                                                <td>{index + 1}</td>
                                                                <td>
                                                                    <strong>{item.product_name}</strong>
                                                                    {item.product_id && (
                                                                        <small className="d-block text-muted">ID: #{item.product_id}</small>
                                                                    )}
                                                                </td>
                                                                <td>
                                                                    <span className="badge bg-light text-dark">
                                                                        {item.sku || 'N/A'}
                                                                    </span>
                                                                </td>
                                                                <td className="text-end">{item.original_quantity || item.quantity || 0}</td>
                                                                <td className="text-end fw-bold">{item.return_quantity || item.quantity || 0}</td>
                                                                <td className="text-end">{formatCurrency(item.unit_price)}</td>
                                                                <td className="text-end">{formatCurrency(item.discount || 0)}</td>
                                                                <td className="text-end">{formatCurrency(item.tax_amount || 0)}</td>
                                                                <td className="text-end fw-bold text-primary">{formatCurrency(item.total_cost)}</td>
                                                                <td>
                                                                    {item.reason ? (
                                                                        <span className="badge bg-info">{item.reason}</span>
                                                                    ) : (
                                                                        <span className="text-muted">-</span>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ))}
                                                    </tbody>
                                                    <tfoot className="bg-light">
                                                        <tr>
                                                            <td colSpan="8" className="text-end fw-bold">Subtotal:</td>
                                                            <td className="text-end fw-bold">{formatCurrency(returnDetails.subtotal)}</td>
                                                            <td></td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="8" className="text-end">Tax:</td>
                                                            <td className="text-end">{formatCurrency(returnDetails.tax)}</td>
                                                            <td></td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="8" className="text-end">Shipping:</td>
                                                            <td className="text-end">{formatCurrency(returnDetails.shipping || 0)}</td>
                                                            <td></td>
                                                        </tr>
                                                        <tr>
                                                            <td colSpan="8" className="text-end fw-bold">Grand Total:</td>
                                                            <td className="text-end fw-bold text-primary">{formatCurrency(returnDetails.grand_total)}</td>
                                                            <td></td>
                                                        </tr>
                                                    </tfoot>
                                                </table>
                                            </div>
                                        ) : (
                                            <div className="text-center py-4">
                                                <p className="text-muted mb-0">No items found for this return</p>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            </div>
                        )}

                        {activeTab === 'payments' && (
                            <div className="tab-pane active">
                                <div className="row">
                                    <div className="col-md-4">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body text-center">
                                                <h6 className="text-muted">Grand Total</h6>
                                                <h4 className="text-primary">{formatCurrency(returnDetails.grand_total)}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body text-center">
                                                <h6 className="text-muted">Paid Amount</h6>
                                                <h4 className="text-success">{formatCurrency(returnDetails.paid_amount)}</h4>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="col-md-4">
                                        <div className="card bg-light mb-3">
                                            <div className="card-body text-center">
                                                <h6 className="text-muted">Due Amount</h6>
                                                <h4 className={returnDetails.due_amount > 0 ? 'text-danger' : 'text-success'}>
                                                    {formatCurrency(returnDetails.due_amount)}
                                                </h4>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="card">
                                    <div className="card-header bg-light">
                                        <h6 className="mb-0">Payment Summary</h6>
                                    </div>
                                    <div className="card-body">
                                        <table className="table table-borderless">
                                            <tbody>
                                                <tr>
                                                    <td width="30%"><strong>Payment Status:</strong></td>
                                                    <td>{getPaymentStatusBadge(returnDetails.payment_status)}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Total Paid:</strong></td>
                                                    <td className="text-success fw-bold">{formatCurrency(returnDetails.paid_amount)}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Remaining Due:</strong></td>
                                                    <td className={returnDetails.due_amount > 0 ? 'text-danger fw-bold' : 'text-success fw-bold'}>
                                                        {formatCurrency(returnDetails.due_amount)}
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Payment Method:</strong></td>
                                                    <td className="text-capitalize">{returnDetails.payment_method || 'Not Specified'}</td>
                                                </tr>
                                                <tr>
                                                    <td><strong>Payment Reference:</strong></td>
                                                    <td>{returnDetails.payment_reference || 'N/A'}</td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                    
                    <div className="modal-footer">
                        <button 
                            type="button" 
                            className="btn btn-secondary" 
                            onClick={onClose}
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

ViewPurchaseReturn.propTypes = {
    returnData: PropTypes.object.isRequired,
    onClose: PropTypes.func.isRequired
};

export default ViewPurchaseReturn;